import { styled } from '@linaria/react';
import { useContext, useMemo } from 'react';
import { icons } from '../../../../assets/icons/icons';
import { UserContext } from '../../../../contexts/user';
import { getGreeting } from '../../../flora-agent/components/agent-landing/agent-landing.helpers';
import { FloraGlowIcon, FloraGradientWash, FloraLogoGlow } from '../flora-landing/flora-landing.styles';
import { FloraPromptChips } from '../flora-landing/flora-prompt-chips';

interface FloraAsideEmptyStateProps {
  onPromptSelect: (prompt: string) => void;
}

/**
 * Opening screen of the Flora side panel for a fresh chat: a greeting over the
 * brand wash plus suggested-prompt chips. It fills the message area, leaving
 * the composer docked at the bottom so nothing moves when the first message
 * is sent.
 *
 * @param {(prompt: string) => void} onPromptSelect - Called with a chip's prompt; the caller guards whether it sends
 * @returns {JSX.Element} The empty-state panel
 */
export function FloraAsideEmptyState({ onPromptSelect }: FloraAsideEmptyStateProps) {
  const { user } = useContext(UserContext);
  const firstName = user?.name?.split(' ')[0];
  const greeting = useMemo(() => getGreeting(firstName), [firstName]);

  return (
    <Wrapper data-testid="flora-aside-empty-state">
      <FloraGradientWash />
      <Content>
        <Hero>
          <FloraLogoGlow>
            <FloraGlowIcon src={icons.iconFlora} alt="" aria-hidden="true" />
          </FloraLogoGlow>
          <HeroText>
            <Greeting>{greeting}</Greeting>
            <Subtitle>Ask about what you're viewing or anything in your portfolio.</Subtitle>
          </HeroText>
        </Hero>
        <ChipsArea>
          <FloraPromptChips onSelect={onPromptSelect} />
        </ChipsArea>
      </Content>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 24px 16px;
  box-sizing: border-box;
  overflow-y: auto;
  background: var(--base-white);
`;

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

/** Insets the chips further than the intro so they don't hug the panel edges. */
const ChipsArea = styled.div`
  width: 100%;
  padding: 0 16px;
  box-sizing: border-box;
`;

const Hero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const Greeting = styled.h2`
  margin: 0;
  font-family: var(--font-family);
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
  color: var(--ink-darkest);
  text-align: center;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: var(--ink-light);
  text-align: center;
  text-wrap: pretty;
`;
